import { Command } from 'cmdk';
import { css, Global } from '@emotion/react';
import {
  Text,
  Flex,
  Icon,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalContent,
  ModalOverlay,
  Box,
  Center
} from '@chakra-ui/react';
import { SearchIcon, HashtagIcon } from '@heroicons/react/solid';
import { DocumentTextIcon } from '@heroicons/react/outline';
import Link from 'next/link';
import { useRouter } from 'next/router';
import type { Hit } from 'instantsearch.js';
import { Highlight } from 'react-instantsearch';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { SearchFetcher } from './search-fetcher';

const searchDialogStyles = css`
  [cmdk-root] {
    border-radius: var(--chakra-radii-2xl);
  }

  [cmdk-input] {
    appearance: none;
    width: 100%;
    border-radius: var(--chakra-radii-2xl);
    padding: 1rem;
    font-size: 1.25rem;
    padding-inline-start: var(--chakra-sizes-12);
    outline: transparent solid 2px;
    outline-offset: 2px;
    color: var(--primaryTextColor);
    background: var(--bodyBg);
  }
`;

function getItemTitle(item: Hit) {
  let title;
  let index = 6;

  // If we are dealing with a content hit, return the highlighted snippet
  if (item.type === 'content') {
    title = <Highlight attribute="content" hit={item} />;
  }

  // Handling getting the proper heading highlight
  while (index > 0 && !title) {
    if (item.hierarchy[`lvl${index}`]) {
      title = <Highlight attribute={`hierarchy.lvl${index}`} hit={item} />;
      break;
    }
    index--;
  }

  return title;
}

function SearchHit({ item, index }: { item: Hit; index: number }) {
  const linkRef = useRef<HTMLAnchorElement>(null);
  const itemRef = useRef<HTMLDivElement>(null);

  /**
   * Addresses an issue where cmdk attempts to scroll to the "selected" item on query change before the selected item is reset to the first item.
   * On query change, cmdk queues a scroll to the selected item. This scroll triggers before the selected item is reset to the "new" first item, causing it to scroll to the "old" first item.
   */
  useEffect(() => {
    if (index === 0 && itemRef.current) {
      // Immediately reset the selected item to the first one in the list
      itemRef.current.setAttribute('aria-selected', 'true');
    }
  }, [index]);

  const handleSelect = useCallback(() => {
    if (linkRef.current) {
      // Simulate clicking the link to navigate, saves us from having to implement custom nav logic
      linkRef.current.click();
    }
  }, []);

  const title = getItemTitle(item);
  const isNestedHeading = item.type.startsWith('lvl') && item.type !== 'lvl1';

  const itemIcon = isNestedHeading ? HashtagIcon : DocumentTextIcon;

  return (
    <Box
      ref={itemRef}
      as={Command.Item}
      borderRadius="md"
      border="1px solid transparent"
      sx={{
        '&[aria-selected]': {
          background: 'var(--highlightBg)',
          border: '1px solid var(--borderColor)'
        },
        mark: {
          background: 'var(--chakra-colors-primary-300)',
          borderRadius: 'sm',
          p: '1px'
        }
      }}
      value={item.objectID}
      onSelect={handleSelect}
    >
      <Flex
        ref={linkRef}
        href={item.url}
        as={Link}
        py={4}
        sx={{
          '&[aria-selected]': {
            background: 'var(--highlightBg)'
          },
          '&:last-of-type': {
            mb: 0
          }
        }}
        color="inherit"
        borderRadius="md"
        mb={2}
      >
        <Center width={12} flexShrink={0}>
          <Icon as={itemIcon} boxSize="5" color="gray.500" />
        </Center>
        <Flex direction="column">
          {(isNestedHeading || item.type === 'content') && (
            <Text fontSize="xs" color="var(--secondaryTextColor)">
              <Highlight attribute={`hierarchy.lvl1`} hit={item} />
            </Text>
          )}

          <Text as="span" flexGrow={1}>
            {title}
          </Text>
        </Flex>
      </Flex>
    </Box>
  );
}

/**
 * Renders a modal that displays search results from `<SearchFetcher />`. Uses Chakra's `<Modal />` component and cmdk's `<Command />` component to handle the UI.
 */
export function SearchDialog({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}) {
  const [results, setResults] = useState<Hit[]>([]);
  const { asPath } = useRouter();

  /**
   * Reset state on navigation
   */
  useEffect(() => {
    setResults([]);
    setIsOpen(false);
  }, [asPath]);

  return (
    <>
      <Global styles={searchDialogStyles} />
      <Modal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
      >
        <ModalOverlay />
        <ModalContent
          pt={0}
          pb={2}
          borderRadius="lg"
          boxShadow="lg"
          backgroundColor="var(--bodyBg)"
          color="var(--primaryTextColor)"
          border="1px solid"
          borderColor="var(--borderColor)"
          {...(results.length === 0 ? { 'data-empty': true } : {})}
          sx={{
            "&[data-empty='true']": {
              pb: 0,
              '& [data-input-group]': {
                borderBottom: 'none',
                mb: 0
              }
            }
          }}
        >
          <Command shouldFilter={false}>
            <SearchFetcher setResults={setResults} />
            <InputGroup
              data-input-group
              mb={2}
              borderBottom="1px solid var(--borderColor)"
            >
              <InputLeftElement pointerEvents="none" height="100%" width="12">
                <Icon
                  as={SearchIcon}
                  boxSize="6"
                  color="gray.400"
                  height="100%"
                />
              </InputLeftElement>
              <Command.Input placeholder="Search docs" />
            </InputGroup>

            <Box as={Command.List} maxHeight="40vh" overflowY="auto" px={2}>
              <Box
                as={Command.Empty}
                p={2}
                pb={4}
                color="var(--secondaryTextColor)"
                textAlign="center"
              >
                No results found.
              </Box>

              {results.map((result, index) => (
                <SearchHit key={result.objectID} item={result} index={index} />
              ))}
            </Box>
          </Command>
        </ModalContent>
      </Modal>
    </>
  );
}
